/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {onJobRerun} from '@ion/applications/src/components/jobs/JobRerunListeners';
import {verifyCrossEnvironmentAccess} from '@ion/applications/src/env/env';
import {
  jobNotificationCreated,
  onNotificationsCleared
} from '@ion/applications/src/notifications/state';
import {changePassword} from '@ion/applications/src/pages/admin/users/state';
import {retrieveReportOptions} from '@ion/applications/src/pages/reports/state';
import initializeUserPreferencesAutoSave, {
  resolvePreferences
} from '@ion/applications/src/preferences/UserPreferencesAutoSave';
import {isDevMode} from '@ion/applications/src/utils/env';
import Datadog from '@ion/core/src/analytics/Datadog';
import Pendo from '@ion/core/src/analytics/Pendo';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {toAccountIdMap} from '@ion/core/src/entities/Accounts';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {get} from '@ion/core/src/environment/environment';
import {error, info} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getRole} from '@ion/core/src/security/helpers';
import {
  getPlatformDeviceTypes,
  getSubAccounts,
  logout
} from '@ion/core/src/services/Connect';
import {
  entityId,
  getMappedSubTypeCategory,
  parseExtendedAttributes
} from '@ion/core/src/services/ConnectUtils';
import Sisense from '@ion/core/src/services/Sisense';
import {
  Entity,
  GenericObject,
  Role,
  User
} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {Preference} from '@ion/core/src/types/preferences';
import {ServiceError} from '@ion/core/src/types/services';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {dehydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {getAccount} from '@ion/services/src/graphql/GraphQL';
import {initializationQuery} from '@ion/services/src/graphql/QueryAdapter';
import getInitializationPerformanceMonitor from '@ion/telemetry/src/apm/monitors/InitializationPerformanceMonitor';
import {
  createFromStatus,
  deleteNotifications,
  onCreated
} from '@/routes/provisioning/jobsearchengine/JobNotifications';
import {runCatchUp} from '@/routes/provisioning/jobsearchengine/JobStatusCatchUp';
import * as JobStatusSubscriptions from '@/routes/provisioning/jobsearchengine/JobStatusSubscriptions';
import {
  setLiveJobsEnabled,
  setSocketConnected
} from '@/routes/provisioning/jobsearchengine/signals/liveUpdates';
import {
  toJobNotification,
  toJobNotifications
} from '@/routes/provisioning/jobsearchengine/toJobNotification';
import {
  getNotifications,
  hydrate as hydrateWatchedJobs
} from '@/routes/provisioning/jobsearchengine/WatchedJobs';
import {watchRerunJob} from '@/routes/provisioning/jobsearchengine/watchRerunJob';
import changePage, {getRoute, isDebug, Route} from '@/routes/RouteDefinitions';
import FeatureFlags, {FeatureFlag} from '@/security/FeatureFlags';
import Features, {
  ADMIN,
  ALERTS,
  ALERTS_SETUP,
  ANALYTICS_REPORTS,
  DASHBOARD_PAGE,
  EARLY_ACCESS,
  PROVISIONING,
  REPORTS
} from '@/security/Features';
import {hasPermission} from '@/security/useHasPermission';
import {addErrorInterceptor, onFailurePrompt} from '@/store/actions/errors';
import {
  initializePageSecurity,
  setPage
} from '@/store/actions/nav';
import {ApplicationKeys} from '@/types/application';
import '../../props/GlobalProps';
import {
  GLOBAL_GROUP_INITIALIZED,
  refreshAccountHierarchy,
  SET_GLOBAL_GROUP,
  SOLOMON_ID
} from './global-group-chooser';
import {setUsernameFilter} from './jobsearchengine';
import {
  initializeEntityObserver,
  isSocketConnected,
  onSocketConnectionChange
} from './websocket';

export const PRE_INITIALIZATION_REQUEST          = 'PRE_INITIALIZATION_REQUEST';
export const PRE_INITIALIZATION_COMPLETE         = 'PRE_INITIALIZATION_COMPLETE';
export const PRE_INITIALIZATION_ERROR            = 'PRE_INITIALIZATION_ERROR';

export const APPLICATION_INITIALIZATION_REQUEST  = 'APPLICATION_INITIALIZATION_REQUEST';
export const APPLICATION_INITIALIZATION_COMPLETE = 'APPLICATION_INITIALIZATION_COMPLETE';
export const APPLICATION_INITIALIZATION_ERROR    = 'APPLICATION_INITIALIZATION_ERROR';

export const POST_INITIALIZATION_REQUEST         = 'POST_INITIALIZATION_REQUEST';
export const POST_INITIALIZATION_COMPLETE        = 'POST_INITIALIZATION_COMPLETE';
export const POST_INITIALIZATION_ERROR           = 'POST_INITIALIZATION_ERROR';

export const REFRESH_INITIALIZATION_REQUEST      = 'REFRESH_INITIALIZATION_REQUEST';
export const REFRESH_INITIALIZATION_COMPLETE     = 'REFRESH_INITIALIZATION_COMPLETE';
export const REFRESH_INITIALIZATION_ERROR        = 'REFRESH_INITIALIZATION_ERROR';

export const INSUFFICIENT_PERMISSIONS_ERROR      = 'INSUFFICIENT_PERMISSIONS_ERROR';

// Variable name for localStorage
export const USER_NAME_VERIFICATION_KEY = 'LOGIN_USERNAME';

let disposeJobStatusListener: (() => void) | null  = null;
let disposeClearedListener: (() => void) | null    = null;
let disposeCreatedListener: (() => void) | null    = null;
let disposeRerunListener: (() => void) | null      = null;
let disposeConnectionListener: (() => void) | null = null;

export const metricsBegin = () => {
  const monitor = getInitializationPerformanceMonitor();
  if (monitor && !monitor.isRunning()) {
    monitor.start();
  }
};
export const metricsEnd = () => {
  const monitor = getInitializationPerformanceMonitor();
  if (monitor?.isRunning()) {
    monitor.stop();
  }
};

/**
 * Provides the application pre-authentication initialization action. This
 * action manages all pre-initialization configurations required prior to
 * authentication.
 */
export const preinitialization = () => {
  info('Application Pre Initialization ...');
  return dispatch => {
    addErrorInterceptor(dispatch);
    info('Application Pre-initialization Complete');
    dispatch({type: PRE_INITIALIZATION_COMPLETE});
  };
};

/**
 * Provides a convenience which function verifies that the current user call returns
 *  the same username as that which was used when authenticating with CTC.
 */
const verifyCurrentUser = (user: User, dispatch: Function) => {
  const verifiedUsername = getLocalStorage(USER_NAME_VERIFICATION_KEY);
  const currentUsername  = user.username.toLowerCase();
  const isValid = localStorage && (verifiedUsername === currentUsername);
  if (!isValid) {
    error(`CTC Admin SESSION ERROR: Invalid Current User Response ${currentUsername}. Verified user is: ${verifiedUsername}`);
    Pendo.track('CTC Admin SESSION ERROR', {
      severity : 'FATAL',
      currentUsername,
      verifiedUsername
    });
    deleteLocalStorage(USER_NAME_VERIFICATION_KEY);
    dispatch(logout());
    return false;
  }
  return true;
};

/**
 * Provides the application post-authentication initialization action. This
 * action manages all post-authentication initialization requests required
 * by the application prior to rendering the first page.
 */
export const initialize = (user: User, route?: any, isFromLegacyAdmin = false) => {
  return async(dispatch, getState) => {
    info('Application Initialization ...');
    UserPreferences.persistDefaultLanguage();
    dispatch({type: APPLICATION_INITIALIZATION_REQUEST, route});
    const liveJobs = FeatureFlags.isEnabled(FeatureFlag.LIVE_JOBS);
    setLiveJobsEnabled(liveJobs);
    if (liveJobs) {
      initializeEntityObserver().catch(reason => {
        error(`WebSocket initialization failed: ${reason}`);
      });
    }
    try {
      setLocalStorage(USER_NAME_VERIFICATION_KEY, user?.username?.toLowerCase());
      dispatch(setUsernameFilter(user.username.toLowerCase()));
      const {enriched, account} = user;
      const roles               = enriched.roles;
      const appKey              = get('appKey');
      const accountId           = entityId(account);
      const userRole            = getRole(roles, appKey);
      const solomonId           = enriched?.account?.solomonId;

      if (!isDevMode()) {
        Pendo.initialize(user, {application: ApplicationKeys.CTCAdmin, solomonId});
        if (Datadog.isEnabled()) {
          Datadog.identifyUser({
            userId      : user.id,
            username    : user.username,
            accountName : user.account?.title,
            accountId
          });
        }
      }

      const {role, application} = await initializationQuery(userRole.id);
      const attributes          = parseExtendedAttributes(application, true);
      const hasReportsFlag      = FeatureFlags.isEnabled(FeatureFlag.REPORTS);
      let features              = reorderFeatures(attributes.features);
      features                  = getUpdatedFeatures(hasReportsFlag, features);

      const allRoles = roles.reduce((acc: Role[], item: Role) => {
        item.id === role.id ? acc.unshift(role) : acc.push(item);
        return acc;
      }, []);

      const userRoleExtendedAttr = parseExtendedAttributes(allRoles?.[0], true);
      const hasProvisioning      = userRoleExtendedAttr?.features?.filter(({id}) => id === PROVISIONING);

      const {
        deviceTypes,
        mappedPlatformIds
      } = hasProvisioning.length > 0 ? await getDeviceTypeFileTypes() || {} : {};

      const hasUpdateUserPermission = hasPermission(role?.permissions,
        Entity.user.concat('-', Operation.Update)
      );

      await initializePreferences(user, !hasUpdateUserPermission);
      const catchUpAccount = () => getState()?.globalGroup?.globalGroupId || accountId;

      disposeCreatedListener?.();
      disposeCreatedListener = onCreated(function(entry) {
        dispatch(jobNotificationCreated(toJobNotification(entry)));
      });

      disposeRerunListener?.();
      disposeRerunListener = onJobRerun(watchRerunJob);

      disposeJobStatusListener?.();
      disposeJobStatusListener = JobStatusSubscriptions.onTerminal(function(status, jobId) {
        createFromStatus(jobId, status);
      });
      disposeClearedListener?.();
      disposeClearedListener = onNotificationsCleared(deleteNotifications);
      disposeConnectionListener?.();
      setSocketConnected(isSocketConnected());
      disposeConnectionListener = onSocketConnectionChange(setSocketConnected);
      JobStatusSubscriptions.start(function() {
        runCatchUp(catchUpAccount());
      });
      if (liveJobs) {
        runCatchUp(catchUpAccount());
      }

      const accounts = await getSubAccounts(accountId, true);
      Features.setAdditionalPermissions(role?.permissions);
      Features.initialize({
        enableLogging     : isDebug(),
        allFeatures       : features,
        roles             : allRoles,
        resolved          : true,
        mappedPlatformIds : mappedPlatformIds,
        application,
        deviceTypes,
        appKey,
        user
      });
      Features.updateRoleFeatures(FeatureFlags.getRoleFeatures());
      const isPermitted = verifyCrossEnvironmentAccess(Features) !== false;
      if (isPermitted) {
        dispatch(initializationComplete({accounts, role, user}, isFromLegacyAdmin, route));
      }
    } catch (err) {
      error(err);
      const isPermissionsError = (err as ServiceError)?.isPermissionsError;
      if (isPermissionsError) {
        deleteLocalStorage(ApplicationKeys.CTCAdminSession);
        deleteLocalStorage(ApplicationKeys.CTCAdminPage);
      };
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, isForbidden: isPermissionsError});
      if (!isPermissionsError) {
        dispatch(onInitializationFailure(true));
      }
    }
  };
};

/**
 * Defines the Get Device Type File Type which is invoked upon successful
 * initialization of all Plat Form Ids for the available Device Type and File Types.
 */
export const getDeviceTypeFileTypes = async() => {
  let deviceTypeFileTypes;
  let mappedPlatformIds = {};
  try {
    deviceTypeFileTypes = await getPlatformDeviceTypes(true);
    mappedPlatformIds   = getMappedSubTypeCategory(deviceTypeFileTypes);
  } catch (err) {
    error('Unable to retrieve the Device Type File Type');
  }
  return {
    deviceTypes : deviceTypeFileTypes,
    mappedPlatformIds
  };
};

/**
 * Defines the initialization complete action which is invoked upon successful
 * initialization of all post-authentication service calls, after which, the
 * first page is ready to be rendered.
 */
export const initializationComplete = (items, isFromLegacyAdmin = false, route = null) => {
  return async(dispatch, getState) => {
    const {user} = items;
    EntityLabel.initialize(items?.role);
    dispatch({type: APPLICATION_INITIALIZATION_COMPLETE, ...items, isFromLegacyAdmin, route});
    const defaultGroupId = UserPreferences.get(Preference.defaultGroupId);
    const {accountHierarchy, globalGroupId} = getState().globalGroup;
    if (defaultGroupId) {
      const accountsMap  = toAccountIdMap(accountHierarchy);
      const defaultGroup = accountsMap[defaultGroupId] || accountsMap[globalGroupId];
      Features.setCurrentAccount(defaultGroup);
      dispatch({type: SET_GLOBAL_GROUP, group: defaultGroup});
      Sisense.setAccountId(defaultGroupId);
    }
    dispatch({type: GLOBAL_GROUP_INITIALIZED});
    const features = {
      Features,
      DASHBOARD_PAGE,
      REPORTS,
      ANALYTICS_REPORTS
    };
    let solomonId = user.enriched?.account?.solomonId;
    if (entityId(user.account) !== defaultGroupId || isUndefined(solomonId)) {
      const id = defaultGroupId || globalGroupId;
      try {
        solomonId = await getAccount({
          fields : ['solomonId'],
          args   : {
            id
          }
        });
      } catch (err) {
        error(`Unable to retrieve solomonId for account: ${id}. Error: ${err?.toString()}`);
      }
    }
    dispatch({type: SOLOMON_ID, solomonId});
    dispatch(retrieveReportOptions(false, features));
    let defaultPage         = getRoute(getState().nav.defaultPage);
    const hasPreviewFeature = getState().login.hasPreviewFeature;
    const currentPage = getRoute(getState().nav.currentPage);
    if (currentPage === getRoute(Route.LOGIN)) {
      defaultPage = getRoute(Route.DASHBOARD);
    }
    dispatch(initializePageSecurity());
    if (hasPreviewFeature) {
      dispatch(setPage(defaultPage));
      changePage(defaultPage as Route);
      info('Application Initialization Complete');
      dispatch(postInitialize());
    } else {
      await logout();
      dispatch({type: INSUFFICIENT_PERMISSIONS_ERROR});
    }
    verifyCurrentUser(items.user, dispatch);
    if (items.user && !items.user.passwordUpdated) {
      dispatch(changePassword(true));
    }
  };
};

/**
 * Set User Preferences based on login user permissions
 */
const initializePreferences = async(user: User, readOnly = false) => {
  const {preferences, lastModifiedOn} = UserPreferences.fromExtendedAttributes(user);
  const {preferences:current, isStale} = resolvePreferences(
    user.id, lastModifiedOn, preferences
  );
  await UserPreferences.initialize(
    user.id,
    current,
    ApplicationKeys.CTCAdmin,
    readOnly,
    true
  );
  hydrateWatchedJobs();
  initializeUserPreferencesAutoSave(user.id);
  if (isStale) {
    UserPreferences.save();
  }
};

/**
 * Defines the post-initialization action which is invoked upon successful
 * initialization of all initialization service calls. The post initialization
 * process comprises non-blocking requests which are made in the background
 * subsequent to the first page being rendered and the application being ready
 * for use by the user.
 */
export const postInitialize = () => {
  return async dispatch => {
    info('Application Post Initialization ...');
    dispatch({type: POST_INITIALIZATION_REQUEST});
    try {
      const notifications = toJobNotifications(getNotifications());
      info('Application Post Initialization Complete');
      dispatch({type: POST_INITIALIZATION_COMPLETE, notifications});
    } catch (err) {
      error(err);
      dispatch({type: POST_INITIALIZATION_ERROR});
      dispatch(onInitializationFailure());
    }
  };
};

/**
 * Invoked any time after initialization has completed (pre, post, etc.) when a
 * specific piece of initialization state needs to be refreshed (such as when an
 * account is added to the users account hierarchy, etc.).
 */
export const refreshInitialization = () => {
  return async dispatch => {
    const currentUser = dehydrateCurrentUser();
    info('Application Initialization Refresh ...');
    dispatch({type: REFRESH_INITIALIZATION_REQUEST});
    try {
      const accounts = await getSubAccounts(entityId(currentUser.account), true);
      dispatch({type: REFRESH_INITIALIZATION_COMPLETE, accounts});
      dispatch(refreshAccountHierarchy(accounts));
    } catch (err) {
      error(err);
      dispatch({type: APPLICATION_INITIALIZATION_ERROR, error: err});
      dispatch(onInitializationFailure());
    }
  };
};

/**
 * Provides an abstraction of the `onFailurePrompt` action such that pre-defined,
 * initialization specific option overrides are provided. This action is dispatched
 * internally when a catch block is executed within any of the various initialization
 * actions.
 *
 * @param isInitialization When `true`, indicates that the initialization error has
 *        occurred within the `initialization` action creators - at which point the
 *        User Prompt will display a "Dismiss" CTA. Otherwise, when `false` (default)
 *        the User Prompt will display a "Reload" CTA, which, when clicked, will reload
 *        the application. This is necessary as the user will at this point be on the
 *        first page and we do not want them to continue use if the application is in
 *        a potentially unpredictable state.
 */
const onInitializationFailure = (isInitialization = false) => {
  return dispatch => {
    dispatch(onFailurePrompt({
      message     : 'notifications:error.initialization',
      actionLabel : `notifications:error.${isInitialization ? 'dismiss' : 'reload'}`,
      onAction    : !isInitialization ? function() {
        globalThis.location.reload();
      } : undefined
    }));
  };
};

/**
 * TODO > FEATURES UPDATE [ADM-2245]: Remove & Rely on Seed Script Feature Order
 *
 * Reorder the features based on the ADM structure.
 */
const reorderFeatures = (features) => {
  if (features) {
    const hasReports = features.findIndex(({id}) => id === REPORTS || id === ANALYTICS_REPORTS);
    const hasNotifications = features.findIndex(({id}) => id === ALERTS);
    if (hasReports > -1 && hasNotifications > -1) {
      const reports = features[hasReports];
      const notifications = features[hasNotifications];
      features.splice(hasReports, 1);
      features.splice(hasNotifications, 0, reports);

      const notificationFeatures = notifications?.features;
      const hasNotificationSetup = notificationFeatures?.findIndex(({id}) => id === ALERTS_SETUP);
      if (notificationFeatures && hasNotificationSetup > -1) {
        const notificationSetup = notificationFeatures[hasNotificationSetup];
        notificationFeatures.splice(hasNotificationSetup, 1);
        const admin = features.find(({id}) => id === ADMIN);
        const adminFeatures = admin?.features;
        if (adminFeatures?.length) {
          adminFeatures.splice(adminFeatures.length - 2, 0, notificationSetup);
        }
      }
    }
  }
  return features;
};

/**
 * Filter the features by feature id.
 */
export const getFilterFeature = (features, featureId) => {
  return features?.find(feature => feature.id === featureId);
};

const REPORTS_FLAG_FEATURE_IDS = [ANALYTICS_REPORTS, EARLY_ACCESS];

/**
 * Excludes the Analytics & Reports and Early Access features when the reports
 * feature flag is disabled.
 *
 * TODO: Remove once the reports feature flag is removed.
 */
export const getUpdatedFeatures = (
  hasFlag: boolean,
  featureList?: GenericObject[] | null
): GenericObject[] | null | undefined => (
  hasFlag ? featureList : featureList?.filter(({id}) => !REPORTS_FLAG_FEATURE_IDS.includes(id))
);
